import axios from 'axios';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useModalStore } from './modal';
import { useToastStore } from './toast';

export interface CartState {
  version: string
  count: number
}

export const useCartStore = defineStore('cart', () => {
    // State using ref
    const version = ref('1.0');
    const count = ref(0);

    // Getters using computed
    const cartCount = computed(() => count.value);

    // Actions as regular functions
    function setLoading(loading: boolean) {
        const modalStore = useModalStore();
        if (loading) {
            modalStore.startLoading();
        } else {
            modalStore.stopLoading();
        }
    }

    async function getCart() {
        setLoading(true);
        try {
            const response = await axios.get(`${import.meta.env.VUE_APP_API_URL}bestelsite/carts`);
            setLoading(false);
            return response;
        } catch (error) {
            setLoading(false);
            throw error;
        }
    }

    async function updateCart(data: any, productInfo?: { name: string, displayQuantity: number }) {
        setLoading(true);
        try {
            await axios.put(`${import.meta.env.VUE_APP_API_URL}bestelsite/carts`, data);
  
            setLoading(false);
            await updateCartCount();
      
            // Show success toast with product info if available
            const toastStore = useToastStore();
            if (productInfo) {
                const truncatedName = productInfo.name.length > 30 
                    ? `${productInfo.name.slice(0, 30)}...` 
                    : productInfo.name;
        
                if (data.quantity === 0) {
                    toastStore.showSuccess(`${truncatedName} verwijderd`, 3000);
                } else {
                    toastStore.showSuccess(`${productInfo.displayQuantity}X ${truncatedName} in winkelmandje`, 3000);
                }
            } else {
                toastStore.showSuccess('Product toegevoegd aan winkelmand', 3000);
            }
      
            // Get updated cart data to return
            const updatedCart = await getCart();
            return updatedCart;
        } catch (error) {
            setLoading(false);
            throw error;
        }
    }

    async function updateCartCount() {
        const response = await axios.get(`${import.meta.env.VUE_APP_API_URL}bestelsite/carts/count`);
        count.value = response.data;
        return response;
    }

    async function sendCart(data: any) {
        setLoading(true);
    
        try {
            const response = await axios.post(`${import.meta.env.VUE_APP_API_URL}bestelsite/orders`, data);
      
            setLoading(false);
            await updateCartCount();
      
            // Get cart data for tracking
            const cart = await getCart();
      
            // Create structured items for dataLayer tracking
            const structuredItems = cart.data?.data?.map((itemData: any, index: number) => {
                const { item } = itemData;

                return {
                    index,
                    item_id: item.no,
                    item_name: item.name,
                    item_brand: item.color,
                    item_category: item.country?.name || '',
                    item_category2: item.region?.name || '',
                    item_category3: item.estate?.name || '',
                    item_category4: `${item.alcohol_percentage}%`,
                    item_category5: item.grapes?.map((grape: any) => grape.name).join(', ') || '',
                    price: itemData.price,
                    quantity: itemData.quantity,
                };
            });
      
            const dataLayer = (window as any).dataLayer || [];
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: 'purchase',
                ecommerce: {
                    transaction_id: 'T_12345',
                    value: cart.data?.total_amount || 0,
                    currency: 'EUR',
                    items: structuredItems,
                },
            });
      
            return response;
        } catch (error) {
            setLoading(false);
            await updateCartCount();
            throw error;
        }
    }

    async function getTimes() {
        const response = await axios.get(`${import.meta.env.VUE_APP_API_URL}bestelsite/times`);
        return response;
    }

    return {
    // State
        version,
        count,
        // Getters
        cartCount,
        // Actions
        setLoading,
        getCart,
        updateCart,
        updateCartCount,
        sendCart,
        getTimes,
    };
}); 
